import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import Login from "./Login";
import SignUp from "./SignUp";
import Alert from "./Alert";
import { setCaseDbCsrfToken } from '../utils/csrf';
import '../Context.css';
import '../index.css';

const Context = ({ setAccessLevel, setUser, setLoggedIn, setShowAlert, showAlert }) => {
  const [formData, setFormData] = useState({ user: '', password: '', confirm_password: '', email: '' });
  const [close, setClose] = useState(false);
  const [error, setError] = useState(null);
  const [showLegacyLogin, setShowLegacyLogin] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const location = useLocation();
  const navigate = useNavigate();
  const isLogin = location.pathname === '/login';

  useEffect(() => {
    document.title = isLogin ? "Login | CaseDB" : "Sign Up | CaseDB";

    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      // Auto-show legacy login on large screens
      if (window.innerWidth > 1024) {
        setShowLegacyLogin(true);
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Set initial state based on screen size
    if (window.innerWidth > 1024) {
      setShowLegacyLogin(true);
    }

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = (e) => {
    e.preventDefault();
    fetch(`https://api.casedb.co/${isLogin ? 'login' : 'signup'}.php`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setCaseDbCsrfToken(data.csrf_token);
          if (isLogin) {
            localStorage.setItem('token', data.token);
            setClose(true);
            setUser(data);
            setTimeout(() => {
              setLoggedIn(true);
              const accessLevel = data.access_level || 'user';
              const defaultRoute = accessLevel === 'global admin' ? '/dashboard' : '/cases';
              setAccessLevel(accessLevel);
              const redirectTo = location.state?.from || defaultRoute;
              navigate(redirectTo);
            }, 2000);
          } else {
            navigate('/login');
          }
        } else {
          if (data.error === 'User awaiting access from admin') {
            setShowAlert({ message: data.error, type: 'info' });
          } else {
            setError(data.error || 'An error occurred.');
          }
        }
      })
      .catch((err) => {
        console.error('Error during fetch:', err); // Debugging log
        setError('An error occurred');
      });
  };

  const formUpdate = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <div id='context' style={{ animation: close ? 'fade-out 2s forwards' : '' }}>
      {error && <Alert message={error} type="error" onClose={() => setError(null)} />}
      <form id='context-form' onSubmit={fetchData}>
        {window.innerWidth <= 1024 && <h2>Your data. All in. <span style={{ fontWeight: "900" }}>All the time.</span></h2>}
        
        {/* Primary Login - Microsoft */}
        <button
          type="button"
          className="microsoft-login"
          onClick={() => {
            const tenantId = "5a29d260-1076-4d36-b1a8-59564b8f3067";
            const clientId = "2c0e226b-2b15-4716-b743-ff7d4627c06a";
            const redirectUri = encodeURIComponent("https://api.casedb.co/graph_callback.php");
            const scope = encodeURIComponent("User.Read Calendars.ReadWrite Mail.Send offline_access");
            
            // Capture the intended redirect path from location state or current path
            const intendedPath = location.state?.from || window.location.pathname + window.location.search;
            const state = encodeURIComponent(JSON.stringify({
              random: Math.random().toString(36).substring(2, 15),
              redirectPath: intendedPath
            }));

            const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&response_mode=query&scope=${scope}&state=${state}`;
            window.location.href = authUrl;
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" height={16} width={16}><path fill="#f35325" d="M0 0h10v10H0z"/><path fill="#81bc06" d="M11 0h10v10H11z"/><path fill="#05a6f0" d="M0 11h10v10H0z"/><path fill="#ffba08" d="M11 11h10v10H11z"/></svg>
          {isLogin ? 'Sign In with Microsoft' : 'Continue with Microsoft'}
        </button>
        
        {/* Fallback Option Divider or Toggle Button */}
        {showLegacyLogin ? (
          <span className='or'><div className='divider horizontal'></div>Use Legacy Login<div className='divider horizontal'></div></span>
        ) : (
          <button
            type="button"
            className='fallback-toggle-btn'
            onClick={() => setShowLegacyLogin(true)}
          >
            Use Legacy Login
          </button>
        )}
        
        {/* Fallback Login - Traditional (only show if toggled on) */}
        {showLegacyLogin && (
          <>
            {isLogin ? <Login formUpdate={formUpdate} formData={formData} /> : <SignUp formUpdate={formUpdate} formData={formData} />}
            <button type="submit" className='main-act-btn fallback-btn'>
              {isLogin ? 'Login with Password' : 'Create Account'}
            </button>
            {/*<button type='button' className='change-context' onClick={() => navigate(isLogin ? '/signup' : '/login')} aria-label="Switch to Login">
              {isLogin ? 'Need an account?' : 'Already have an account?'}
            </button>*/}
          </>
        )}
      </form>
      <div id="context-bg">
        <div id="context-bg-container">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
        {window.innerWidth > 1024 && <h2>Your data. All in. <span style={{ fontWeight: "900" }}>All the time.</span></h2>}
      </div>
      {showAlert && (
        <Alert
          message={typeof showAlert === 'object' ? showAlert.message : 'User logged out.'}
          type={typeof showAlert === 'object' ? showAlert.type : 'info'}
          onClose={() => setShowAlert(false)}
        />
      )}
    </div>
  );
}

export default Context;